import { addMonths, format, getYear, isValid, parse, parseISO } from 'date-fns';

import { toNumber } from 'lodash-es';

/**
 * Converts any given date format into expected date format, can be used even for time conversion.
 * Refer date-fns formats to specify the inputFormat and outputFormat string
 * @param {string} date - date string in any format
 * @param {string} inputFormat - format string of first param
 * @param {string} outputFormat - format string of expected date format
 * @returns {string} - formatted date string
 */
const formatDateTime = (dateTime, inputFormat, outputFormat) => {
	try {
		const parsedDate = parse(dateTime, inputFormat, new Date());
		const formattedDate = format(parsedDate, outputFormat);
		return formattedDate;
	} catch (error) {
		return 'Invalid Date';
	}
};

/**
 * @returns {string} - current financial year in format YYYY-YYYY
 */
const getCurrentFiscalYear = () => {
	const now = new Date();
	const currentYear = format(now, 'yyyy');
	const nextYear = toNumber(currentYear) + 1;
	const currentMonth = Number(format(now, 'MM'));
	const fiscalYear =
		currentMonth < 4
			? `${currentYear - 1}-${currentYear}`
			: `${currentYear}-${nextYear}`;
	return fiscalYear;
};

/**
 * Generates list of fiscal year with label and value
 * @param {number} startingYear
 * @returns {array} - list of fiscal year in format
 * [
 *    {
 *      label: '2021-22',
 *      value: '2021-2022'
 *    },
 *    {
 *      label: '2022-23',
 *      value: '2022-2023'
 *    }
 * ]
 */
function getFinancialYearList(startingYear) {
	const startingYearInt = parseInt(startingYear, 10);
	const isYearInRange = startingYearInt >= 1000 && startingYearInt <= 9999;
	if (Number.isNaN(startingYearInt) || !isYearInRange) {
		return [];
	}
	const now = new Date();
	const currentYear = getYear(now);
	const startYear = startingYear || currentYear;

	const endYearDate = addMonths(now, 3);
	const endYear = getYear(endYearDate);
	const fiscalYearCount = endYear - startYear + 1;

	if (fiscalYearCount <= 0) return [];

	const fiscalYearArr = [];
	for (let i = 0; i < fiscalYearCount; i += 1) {
		const start = startYear + i;
		const end = start + 1;
		fiscalYearArr.push({
			label: `${start}-${end.toString().slice(2)}`,
			value: `${start}-${end}`,
		});
	}

	return fiscalYearArr;
}
/**
 * Checks any given date is valid according to iSO or not.
 * @param {string} date - date string in any format
 * @returns {boolean} -  date is valid or not
 */
const isValidDateTime = (dateTime) => {
	try {
		const parsedDate = parseISO(dateTime);
		return isValid(parsedDate);
	} catch (error) {
		return false;
	}
};

const commonDateService = {
	formatDateTime,
	getCurrentFiscalYear,
	getFinancialYearList,
	isValidDateTime,
};

export default commonDateService;
